import { Component, OnInit } from '@angular/core';
import { NgZone } from '@angular/core';
import { LinkedLoginAttemptKey, OktaAuthService } from '../../../services/okta-auth.service';
import { UserService } from '../../../services/user-service.service';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { HybridStorageKeys, HybridStorageService } from '../../services/hybrid-storage.service';
import { DemoModeService } from '../../../services/demo-mode.service';
import { IDToken } from '@okta/okta-auth-js';
import { LoginDebugService } from '../../../services/login-debug.service';
import { MatDialog } from '@angular/material/dialog';
import { LoginDiagnosticsModalComponent } from '../../apps/shared/login-diagnostics-modal/login-diagnostics-modal.component';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { ProfileNotFoundError } from '../../models/profile-not-found.error';
import { loginDebugAlert } from '../../../services/login-debug-alert';

@Component({
    selector: 'app-login-callback',
    template: '',
})
export class LoginCallbackComponent implements OnInit {
  private isHandlingRedirect = false;

  // One-shot guard (survives the sign-in redirect round-trip via sessionStorage, same pattern as
  // LinkedLoginAttemptKey) so a recoverable failure auto-restarts the sign-in exactly once and a
  // broken environment can't loop. Cleared on a completed login and when the error screen is shown.
  private static readonly AutoRestartKey = 'login-auto-restart-attempted';

  constructor(
    private oktaAuth: OktaAuthService,
    private userService: UserService,
    private router: Router,
    private zone: NgZone,
    private hybridStorageService: HybridStorageService,
    private demoModeService: DemoModeService,
    private loginDebugService: LoginDebugService,
    private matDialog: MatDialog
  ) {}

  async ngOnInit() {
    if (this.isHandlingRedirect) {
      return;
    }
    this.isHandlingRedirect = true;

    try {
      // Ensure hybrid storage is ready before processing tokens
      await this.hybridStorageService.ready;

      if (Capacitor.isNativePlatform()) {
        const url = window.location.href;
        this.oktaAuth.isHandlingRedirect = false;
        await this.oktaAuth.handleOktaRedirect(url);
      } else {
        await this.oktaAuth.getOktaAuth().handleRedirect();
      }

      await this.userService.setUserProfile();
      await this.userService.emitAuthStatus();
      await this.logDeviceInfoAfterLogin();

      loginDebugAlert('4) Login complete - entering the app');

      // Login completed — lift the linked-login cooldown so a later logout + inbound
      // ?loginType= link in this tab signs in again without waiting it out.
      try {
        sessionStorage.removeItem(LinkedLoginAttemptKey);
        this.clearAutoRestartFlag();
      } catch {}

      // Clear demo mode flag after successful authentication
      if (this.demoModeService.isDemoMode) {
        await this.hybridStorageService.removeItem(HybridStorageKeys.DemoMode);
      }

      let storedDeepLink = await this.hybridStorageService.getItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath);

      if (storedDeepLink) {
        if (storedDeepLink.startsWith('http') || storedDeepLink.startsWith('https')) {
          const url = new URL(storedDeepLink);
          storedDeepLink = url.pathname + url.search;
        }
        const deepLink = storedDeepLink;
        this.zone.run(() => this.router.navigateByUrl(deepLink));
        await this.hybridStorageService.removeItem(HybridStorageKeys.Capacitor_DeepLinkPath);
      } else {
        this.zone.run(() => this.router.navigate(['/mainmenu']));
      }

    } catch (error) {
      console.error('Error handling Okta redirect:', error);
      loginDebugAlert('X) LOGIN FAILED', (error as any)?.message || String(error));

      // Show diagnostics if debug mode enabled
      await this.showLoginCallbackDiagnostics(error);

      // Carry the reason to the error screen. Without this the user just lands back on the
      // login screen with no explanation - which reads to them as "login did nothing".
      //
      // isProfileNotFound distinguishes the two failures the error screen handles differently:
      //  - true  = authenticated, but no matching account (deprovisioned / not enrolled / wrong login
      //            type). Retrying silently re-authenticates as the same rejected identity and fails
      //            identically, so the error screen offers a full Okta sign-out to break the loop.
      //  - false = a transient/system error. A light retry is right - once it clears, the still-valid
      //            Okta session lets the user back in without re-entering a password.
      const isProfileNotFound = error instanceof ProfileNotFoundError;

      // Some failures can't succeed by retrying THIS callback but ARE fixed by a fresh sign-in:
      // a lost PKCE code_verifier, or a spent/expired authorization code (400 from the exchange).
      // Auto-restart the sign-in ONCE for those - guarded via sessionStorage so a genuinely broken
      // environment can't loop. On the second failure we fall through to the error screen below.
      // Never auto-restart a ProfileNotFound: that just re-authenticates the same rejected identity.
      if (!isProfileNotFound && this.isRecoverableAuthError(error) && !this.autoRestartAlreadyTried()) {
        try {
          this.markAutoRestartTried();
          this.oktaAuth.setIsLoggingIn(true);
          this.oktaAuth.getOktaAuth().tokenManager.clear();
          const redirectUri = await this.oktaAuth.getRedirectUriForLoginType();
          if (redirectUri) {
            await this.oktaAuth.getOktaAuth().signInWithRedirect({ redirectUri });
          } else {
            await this.oktaAuth.getOktaAuth().signInWithRedirect();
          }
          return; // the fresh redirect takes over; don't show the error screen
        } catch (restartError) {
          console.error('[LoginCallback] Auto-restart of sign-in failed:', restartError);
          // fall through to the error screen below
        }
      }

      // Reached the terminal error screen - reset the one-shot guard so a later, manual sign-in
      // attempt gets its own auto-restart chance.
      this.clearAutoRestartFlag();

      const message = isProfileNotFound
        ? error.message
        : 'Something went wrong while signing you in. Please try again in a moment, and contact your school administrator if it keeps happening.';

      this.zone.run(() => this.router.navigate(['/error'], { state: { message, isProfileNotFound } }));
    } finally {
      this.isHandlingRedirect = false;
    }
  }

  /**
   * A failure that a FRESH sign-in would resolve (vs. a transient outage or a bad account).
   * Scoped deliberately narrow: a lost PKCE code_verifier, or a spent/expired authorization code
   * (HTTP 400 from the token exchange). Network/5xx and 401/403 are left to the normal error screen.
   */
  private isRecoverableAuthError(error: any): boolean {
    const msg = (error?.message ?? error?.toString() ?? '').toString();
    return /Missing code_verifier/i.test(msg) || /Token exchange failed with status 400/i.test(msg);
  }

  private autoRestartAlreadyTried(): boolean {
    try {
      return sessionStorage.getItem(LoginCallbackComponent.AutoRestartKey) === '1';
    } catch {
      return false;
    }
  }

  private markAutoRestartTried(): void {
    try {
      sessionStorage.setItem(LoginCallbackComponent.AutoRestartKey, '1');
    } catch {}
  }

  private clearAutoRestartFlag(): void {
    try {
      sessionStorage.removeItem(LoginCallbackComponent.AutoRestartKey);
    } catch {}
  }

  /**
   * Show diagnostics when login callback fails
   */
  private async showLoginCallbackDiagnostics(error: any): Promise<void> {
    if (!this.loginDebugService.isDebugModeEnabled) {
      return;
    }

    try {
      const deviceInfo = await Device.getInfo();
      const networkStatus = await Network.getStatus();
      const accessToken: any = await this.oktaAuth.getOktaAuth().tokenManager.get('accessToken');
      const refreshToken: any = await this.oktaAuth.getOktaAuth().tokenManager.get('refreshToken');

      const diagnostics: any = {
        timestamp: new Date().toISOString(),
        authType: 'Login Callback Failed',
        callSite: 'LoginCallbackComponent.ngOnInit:74',
        networkType: networkStatus.connectionType,
        networkAvailable: networkStatus.connected,
        networkSwitching: false,
        retriesUsed: 0,
        maxRetries: 0,
        success: false,
        platform: deviceInfo.platform,
        osVersion: deviceInfo.osVersion,
        modelName: deviceInfo.model,
        hadCachedProfile: !!this.userService.CurrentUserProfile,
        refreshTokenValid: refreshToken && !this.oktaAuth.getOktaAuth().tokenManager.hasExpired(refreshToken),
        accessTokenValid: accessToken && !this.oktaAuth.getOktaAuth().tokenManager.hasExpired(accessToken),
        error: `Login callback error: ${error?.message || error?.toString() || 'Unknown error'}`
      };

      // Check if mobile - show fullscreen modal
      const isMobile = window.innerWidth <= 768;
      this.matDialog.open(LoginDiagnosticsModalComponent, {
        width: isMobile ? '100vw' : '600px',
        maxWidth: isMobile ? '100vw' : '90vw',
        maxHeight: isMobile ? '100vh' : '90vh',
        height: isMobile ? '100vh' : 'auto',
        panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
        data: diagnostics
      });
    } catch (diagError) {
      console.error('[LoginCallbackDiagnostics] Failed to show diagnostics:', diagError);
    }
  }

  private async logDeviceInfoAfterLogin(): Promise<void> {
    try {
      const rawDeviceInfo = await this.userService.getUserDeviceInfo();

      // Determine authType based on auth_time claim from ID token
      let authType = 'Interactive'; // default
      try {
        const idToken = await this.oktaAuth.getOktaAuth().tokenManager.get('idToken') as IDToken;
        if (idToken && idToken.claims && idToken.claims['auth_time']) {
          const authTime = idToken.claims['auth_time'] as number; // Unix timestamp (seconds)
          const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
          const timeSinceAuth = currentTime - authTime;

          // If auth_time is within last 30 seconds, user likely entered credentials (Interactive)
          // If auth_time is older, Okta session existed and auto-redirected (Silent)
          authType = timeSinceAuth <= 30 ? 'Interactive' : 'Silent';
        }
      } catch (error) {
        console.warn('Could not determine authType from token, defaulting to Interactive:', error);
      }

      let formattedDeviceInfo: any = {
        isMobile: rawDeviceInfo.isMobile,
        timestamp: new Date().toISOString(),
        authType: authType
      };

      if (rawDeviceInfo.isMobile) {
        // Mobile device formatting
        formattedDeviceInfo.mobile = JSON.stringify(rawDeviceInfo);
      } else {
        formattedDeviceInfo.web = {
          userAgent: rawDeviceInfo.userAgent,
          platform: rawDeviceInfo.platform,
          screen: rawDeviceInfo.screen,
          cookieEnabled: rawDeviceInfo.cookieEnabled,
          onLine: rawDeviceInfo.onLine
        };
      }

      await this.userService.logUserDeviceInfo(formattedDeviceInfo);
    } catch (error) {
      console.error('Error logging device info after login:', error);
    }
  }
}
